import CloseIcon from "@mui/icons-material/Close";
import FormatLineSpacingIcon from "@mui/icons-material/FormatLineSpacing";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import Discard from "./discard";
import Invited from "./invited";
import ShortListed from "./shortlisted";
import ToEvaluated from "./to-evaluated";

const InvitedCandidate = ({
  open,
  onClose,
  position,
  initialTab,
  positionId,
}) => {
  const [selectedTab, setSelectedTab] = useState("invited");
  const [questions, setQuestions] = useState([]);

  const [candidates, setCandidates] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [sortBy, setSortBy] = useState("newest");
  const actualPositionId = positionId || position?.id;

  const handleSortClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setAnchorEl(null);
  };
  useEffect(() => {
    const fetchQuestions = async () => {
      if (!position?.id) return;
      try {
        const response = await axios.get(
          `https://api.a2ajobs.com/api/questions?position_id=${position.id}`
        );
        setQuestions(response.data.data.questions);
      } catch (error) {
        console.error("Error fetching questions:", error);
      }
    };

    fetchQuestions();
  }, [position?.id]);

  const handleSortChange = (sortType) => {
    setSortBy(sortType);
    setAnchorEl(null);
    fetchCandidates();
  };

  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `https://api.a2ajobs.com/api/candidate-list?position_id=${actualPositionId}&status=${selectedTab}&search=${searchQuery}&sortby=${sortBy}`
      );
      setCandidates(response.data);
    } catch (error) {
      console.error(
        "Error fetching candidates:",
        error.response?.data || error.message
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedTab(initialTab || "invited");
  }, [initialTab]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchCandidates();
    }, 200);

    return () => clearTimeout(timeout);
  }, [searchQuery, sortBy, refresh, selectedTab]);

  const filterCandidatesByStatus = (status) => {
    if (position && position.id) {
      return candidates?.data?.filter(
        (c) => c.position_id === position.id && c.status === status
      );
    }
    return candidates?.data?.filter((c) => c.status === status);
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen maxWidth="md">
      <Box sx={{ p: { md: 5, xs: 0 }, mx: { md: 10, xs: 0 } }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          padding={2}
        >
          <DialogTitle sx={{ m: 0, p: 0 }}>{position?.title}</DialogTitle>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <DialogContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                fullWidth
                sx={{
                  bgcolor: selectedTab === "invited" ? "#CCD9F5" : "#e0e0e0",
                  color: selectedTab === "invited" ? "#0B56EE" : "#404040",
                  textTransform: "none",
                  borderRadius: "6px",
                }}
                onClick={() => setSelectedTab("invited")}
              >
                Invited
              </Button>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                fullWidth
                sx={{
                  bgcolor: selectedTab === "evaluate" ? "#CCD9F5" : "#e0e0e0",
                  color: selectedTab === "evaluate" ? "#0B56EE" : "#404040",
                  textTransform: "none",
                  borderRadius: "6px",
                }}
                onClick={() => setSelectedTab("evaluate")}
              >
                To Evaluate
              </Button>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                fullWidth
                sx={{
                  bgcolor:
                    selectedTab === "shortlisted" ? "#CCD9F5" : "#e0e0e0",
                  color: selectedTab === "shortlisted" ? "#0B56EE" : "#404040",
                  textTransform: "none",
                  borderRadius: "6px",
                }}
                onClick={() => setSelectedTab("shortlisted")}
              >
                Shortlisted
              </Button>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                fullWidth
                sx={{
                  bgcolor: selectedTab === "discarded" ? "#CCD9F5" : "#e0e0e0",
                  color: selectedTab === "discarded" ? "#0B56EE" : "#404040",
                  textTransform: "none",
                  borderRadius: "6px",
                }}
                onClick={() => setSelectedTab("discarded")}
              >
                Discarded
              </Button>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search in this position"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    fetchCandidates();
                  }
                }}
              />{" "}
            </Grid>
            <Grid item>
              <Box display="flex" gap={1}>
                <Tooltip title="Sort By">
                  <IconButton onClick={handleSortClick}>
                    <FormatLineSpacingIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleSortClose}
            >
              <MenuItem onClick={() => handleSortChange("a-z")}>A-Z</MenuItem>
              <MenuItem onClick={() => handleSortChange("z-a")}>Z-A</MenuItem>
              <MenuItem onClick={() => handleSortChange("newest")}>
                Newest First
              </MenuItem>
              <MenuItem onClick={() => handleSortChange("oldest")}>
                Oldest First
              </MenuItem>
            </Menu>
          </Grid>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress size={50} />
            </Box>
          ) : (
            <>
              {selectedTab === "invited" && (
                <Invited
                  candidates={filterCandidatesByStatus("invited")}
                  setRefresh={setRefresh}
                  position={position}
                  questions={questions}
                  positionId={actualPositionId}
                  fetchCandidates={fetchCandidates}
                />
              )}
              {selectedTab === "evaluate" && (
                <ToEvaluated
                  candidates={filterCandidatesByStatus("evaluate")}
                  setRefresh={setRefresh}
                  questions={questions}
                />
              )}
              {selectedTab === "shortlisted" && (
                <ShortListed
                  candidates={filterCandidatesByStatus("shortlisted")}
                  setRefresh={setRefresh}
                  questions={questions}
                />
              )}
              {selectedTab === "discarded" && (
                <Discard
                  candidates={filterCandidatesByStatus("discarded")}
                  setRefresh={setRefresh}
                />
              )}
            </>
          )}
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default InvitedCandidate;
